import { makeStyles } from "@material-ui/core/styles";
import {
  BLACK_20,
  BLACK_60,
  BLACK,
  BLACK_10,
  BLACK_40,
  BLACK_80,
  PRIMARY,
  PRIMARY_10,
  PRIMARY_20,
  PRIMARY_40,
  PRIMARY_60,
  PRIMARY_80,
  WHITE,
} from "@/theme/theme";

// El tema no define paleta de aviso ni un color por fuente de validación, así que estos valores
// salen del diseño de la HU. Los textos van más oscuros que el mockup para cumplir contraste AA
// sobre el fondo de su tarjeta.
const WARNING = '#F5A623';
const DANGER_TEXT = '#B3261E';
const DGII_TEXT = '#256B2A';
const BILLET_BACKGROUND = '#FFF8E7';
const BILLET_BORDER = '#F3D08A';
const BILLET_TEXT = '#B26A00';
const CARDNET_BACKGROUND = '#EFEFFB';
const CARDNET_BORDER = '#C7C7EE';
const CARDNET_TEXT = '#4B4BB5';

// La app no monta ThemeProvider de Material-UI, así que `color="primary"` renderiza el azul por
// defecto de la librería. El verde de Billet se toma de las constantes del tema.
const useStyle = makeStyles((theme) => ({
  paper: {
    margin: theme.spacing(3),
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  title: {
    color: PRIMARY,
    fontWeight: 600,
  },
  headerActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  primaryButton: {
    textTransform: "initial",
    backgroundColor: PRIMARY,
    color: WHITE,
    "&:hover": {
      backgroundColor: PRIMARY_80,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  secondaryButton: {
    textTransform: "initial",
    color: PRIMARY,
    borderColor: PRIMARY,
    "&:hover": {
      backgroundColor: PRIMARY_10,
      borderColor: PRIMARY,
    },
  },
  filterBar: {
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filterField: {
    minWidth: 180,
    flexGrow: 1,
    maxWidth: 240,
  },
  dateField: {
    minWidth: 150,
    flexGrow: 1,
    maxWidth: 190,
  },
  searchField: {
    minWidth: 240,
    flexGrow: 2,
  },
  clearFilters: {
    marginLeft: "auto",
    textTransform: "initial",
    color: PRIMARY,
    whiteSpace: "nowrap",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  manualRequest: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  sectionsStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  section: {
    border: "1px solid #E6E8EC",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: WHITE,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2, 1),
    backgroundColor: WHITE,
    color: PRIMARY,
  },
  sectionNumber: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    flex: "0 0 auto",
    backgroundColor: PRIMARY,
    color: WHITE,
    fontWeight: 700,
  },
  sectionContent: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
  },
  fieldLabel: {
    display: "block",
    marginBottom: theme.spacing(0.75),
    color: "#102033",
    fontSize: 15,
    fontWeight: 700,
  },
  requiredMark: {
    color: "#E02020",
  },
  stepRadio: {
    color: BLACK_20,
  },
  stepRadioChecked: {
    color: "#0052CC",
  },
  validationBox: {
    border: `1px solid ${PRIMARY}`,
    borderRadius: 4,
    padding: theme.spacing(2),
    backgroundColor: PRIMARY_10,
    color: "#102033",
  },
  validationLine: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.25),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  validationCheck: {
    color: PRIMARY,
    fontSize: 22,
  },
  rncAdornment: {
    color: PRIMARY,
  },
  rncWarningAdornment: {
    color: WARNING,
  },
  rncErrorAdornment: {
    color: "#E02020",
  },
  rncFeedbackCard: {
    border: "1px solid",
    borderRadius: 6,
    padding: theme.spacing(2),
    color: "#102033",
  },
  rncFeedbackSuccess: {
    borderColor: PRIMARY,
    backgroundColor: PRIMARY_10,
  },
  rncFeedbackWarning: {
    borderColor: WARNING,
    backgroundColor: "#FFF8E7",
  },
  rncFeedbackError: {
    borderColor: "#FF5A5F",
    backgroundColor: "#FFF1F1",
  },
  rncFeedbackLine: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.25),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  rncFeedbackMessage: {
    display: "grid",
    gridTemplateColumns: "24px 1fr",
    gap: theme.spacing(1.5),
    alignItems: "flex-start",
  },
  rncFeedbackDetails: {
    marginTop: theme.spacing(1.5),
  },
  rncFeedbackTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1.25),
  },
  rncIncidentList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
  },
  rncIncidentItem: {
    display: "grid",
    gridTemplateColumns: "20px 1fr",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  rncFeedbackSuccessIcon: {
    color: PRIMARY,
    fontSize: 22,
  },
  rncFeedbackWarningIcon: {
    color: WARNING,
    fontSize: 22,
  },
  rncFeedbackErrorIcon: {
    color: "#E02020",
    fontSize: 22,
  },
  rncFeedbackAdverseValue: {
    color: "#E02020",
    fontWeight: 700,
  },
  lockedInput: {
    "& .MuiInputBase-root.Mui-disabled": {
      backgroundColor: "#F8FAFC",
      color: BLACK_20,
    },
  },
  fieldHelper: {
    display: "block",
    marginTop: theme.spacing(0.75),
    color: "#5F6B7A",
    fontSize: 14,
    fontWeight: 600,
  },
  uploadCard: {
    minHeight: 72,
    border: "1px solid #E8ECF0",
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    backgroundColor: WHITE,
    transition: "border-color 0.2s, box-shadow 0.2s",
    "&:hover": {
      borderColor: PRIMARY,
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    },
    "& .MuiIconButton-root": {
      color: BLACK_60,
    },
  },
  uploadPlaceholder: {
    minHeight: 72,
    border: "1px dashed #E8ECF0",
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    backgroundColor: "#FAFBFC",
    transition: "border-color 0.2s, box-shadow 0.2s",
    "&:hover": {
      borderColor: PRIMARY,
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    },
  },
  attachButton: {
    borderColor: PRIMARY,
    borderStyle: "dashed",
    color: PRIMARY,
    backgroundColor: WHITE,
    fontWeight: 700,
    textTransform: "none",
    "&:hover": {
      borderColor: PRIMARY,
      backgroundColor: PRIMARY_20,
    },
  },
  passportCard: {
    border: `1px solid ${PRIMARY_60}`,
    borderRadius: 6,
    padding: theme.spacing(2),
    backgroundColor: PRIMARY_10,
  },
  passportHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
    color: PRIMARY,
    fontWeight: 700,
  },
  hiddenInput: {
    display: "none",
  },
  uploadInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  uploadTitle: {
    color: "#102033",
    fontSize: 14,
    fontWeight: 700,
  },
  uploadEmptyIcon: {
    width: 38,
    height: 38,
    borderRadius: 8,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: WHITE,
    color: PRIMARY,
    border: "1px solid #E8ECF0",
    flex: "0 0 auto",
  },
  uploadFileIcon: {
    width: 38,
    height: 38,
    borderRadius: 8,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FAFBFC",
    color: PRIMARY,
    border: "1px solid #E8ECF0",
    flex: "0 0 auto",
  },
  documentRow: {
    minHeight: 72,
    border: "1px solid #E8ECF0",
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    display: "grid",
    gridTemplateColumns: "minmax(170px, 1.35fr) minmax(180px, 1fr) auto",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.25),
    backgroundColor: WHITE,
    transition: "border-color 0.2s, box-shadow 0.2s",
    "&:hover": {
      borderColor: PRIMARY,
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    },
    "& .MuiIconButton-root": {
      color: BLACK_60,
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      alignItems: "stretch",
    },
  },
  legalDocumentFiles: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  legalDocumentFile: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minWidth: 0,
    padding: theme.spacing(0.5, 0),
  },
  documentStatus: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  addDocumentButton: {
    marginTop: theme.spacing(1),
    borderColor: PRIMARY,
    borderStyle: "dashed",
    color: PRIMARY,
    fontWeight: 700,
    textTransform: "none",
    "&:hover": {
      borderColor: PRIMARY,
      backgroundColor: PRIMARY_20,
    },
  },
  affiliationList: {
    border: "1px solid #D7DCE3",
    borderRadius: 4,
    overflow: "hidden",
    backgroundColor: WHITE,
  },
  affiliationItem: {
    minHeight: 52,
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    gap: theme.spacing(1.25),
    padding: theme.spacing(0.75, 1.5),
    borderBottom: "1px solid #EEF0F2",
    "&:last-child": {
      borderBottom: 0,
    },
  },
  affiliationIcon: {
    width: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: PRIMARY,
    "& svg": {
      fontSize: 28,
    },
  },
  affiliationTitle: {
    color: "#102033",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  affiliationDescription: {
    display: "block",
    color: "#5F6B7A",
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.25,
    marginTop: 2,
  },
  affiliationSwitchBase: {
    color: WHITE,
    "&$affiliationSwitchChecked": {
      color: WHITE,
      transform: "translateX(20px)",
    },
    "&$affiliationSwitchChecked + $affiliationSwitchTrack": {
      backgroundColor: PRIMARY,
      opacity: 1,
    },
  },
  affiliationSwitchChecked: {},
  affiliationSwitchTrack: {
    borderRadius: 12,
    backgroundColor: "#C8CDD5",
    opacity: 1,
  },
  affiliationSwitchThumb: {
    width: 18,
    height: 18,
    boxShadow: "none",
  },
  billetSmartRadioGroup: {
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  billetSmartRadio: {
    color: BLACK_20,
    fontSize: 24,
  },
  billetSmartRadioChecked: {
    color: PRIMARY,
    fontSize: 24,
  },
  billetSmartRadioLabel: {
    color: "#102033",
    fontSize: 15,
    fontWeight: 700,
  },
  greenIcon: {
    color: PRIMARY,
  },
  pdfIcon: {
    color: "#F42626",
  },
  summaryList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.75),
    paddingTop: theme.spacing(1),
  },
  summaryItem: {
    display: "grid",
    gridTemplateColumns: "minmax(130px, 1fr) minmax(140px, 1.1fr)",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(0.5),
    },
  },
  summaryLabel: {
    color: "#5F6B7A",
    fontWeight: 600,
    fontSize: 13,
  },
  summaryValue: {
    color: "#102033",
    fontWeight: 700,
    fontSize: 13,
  },
  draftChip: {
    width: "fit-content",
    justifySelf: "flex-start",
    backgroundColor: "#FFE4B8 !important",
    color: "#7A4B00 !important",
    fontWeight: 700,
    "& .MuiChip-label": {
      color: "#7A4B00",
      fontWeight: 700,
    },
  },
  infoBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(7),
    padding: theme.spacing(2),
    borderRadius: 4,
    backgroundColor: "#E9F2FF",
    color: "#0052CC",
    fontWeight: 700,
  },
  createButton: {
    backgroundColor: PRIMARY,
    color: WHITE,
    borderRadius: 4,
    boxShadow: "none",
    fontWeight: 700,
    "&:hover": {
      backgroundColor: PRIMARY_80,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: PRIMARY_40,
      color: WHITE,
      boxShadow: "none",
    },
  },
  saveButton: {
    backgroundColor: "transparent",
    border: `1px solid ${PRIMARY_60}`,
    color: PRIMARY,
    borderRadius: 4,
    boxShadow: "none",
    fontWeight: 700,
    "&:hover": {
      borderColor: PRIMARY,
      backgroundColor: PRIMARY_10,
      boxShadow: "none",
    },
  },
  backButton: {
    color: PRIMARY,
    fontWeight: 700,
    "&:hover": {
      backgroundColor: PRIMARY_10,
    },
  },
  chip: {
    height: 24,
    fontSize: "0.75rem",
    fontWeight: 500,
  },
  // Cada regla se ancla a `&.MuiChip-root` para ganar en especificidad de CSS. Si usaramos una sola clase se
  // el gris por defecto del Chip  podria tomar precedencia durante el orden de inyección de las hojas de estilos.
  chipDraft: {
    "&.MuiChip-root": {
      backgroundColor: "#FDECC8",
      color: "#7A5C00",
    },
  },
  chipInProgress: {
    "&.MuiChip-root": {
      backgroundColor: "#D7E9F7",
      color: "#125699",
    },
  },
  chipSuccess: {
    "&.MuiChip-root": {
      backgroundColor: PRIMARY_20,
      color: "#256B2A",
    },
  },
  chipError: {
    "&.MuiChip-root": {
      backgroundColor: "#FFDAD6",
      color: "#B3261E",
    },
  },
  // El calendario replica el date-picker de la app de comercios: banda clara entre las fechas y
  // extremos en círculo verde. `.MuiPickersDay-day` da la especificidad para ganarle al estilo base.
  rangeBandDay: {
    backgroundColor: PRIMARY_10,
    borderRadius: 0,
  },
  rangeEndpointDay: {
    '& .MuiPickersDay-day': {
      backgroundColor: PRIMARY,
      color: WHITE,
    },
  },
  // --- Modal de cambios en validaciones ---
  modalPaper: {
    position: 'relative',
    borderRadius: 12,
    maxWidth: 420,
  },
  modalClose: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: BLACK_20,
  },
  modalContent: {
    padding: theme.spacing(4, 4, 1),
    textAlign: 'center',
  },
  modalIcon: {
    fontSize: 48,
    color: WARNING,
  },
  modalTitle: {
    marginTop: theme.spacing(1),
    fontSize: '1.25rem',
    fontWeight: 700,
    color: BLACK,
  },
  modalDescription: {
    marginTop: theme.spacing(1.5),
    fontSize: '0.875rem',
    color: BLACK_40,
  },
  changeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    textAlign: 'left',
  },
  changeCard: {
    border: '1px solid',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
  },
  // Cada fuente colorea la tarjeta entera; el icono y el título heredan ese color en vez de
  // repetirlo en tres clases más.
  changeCardDgii: {
    backgroundColor: PRIMARY_10,
    borderColor: PRIMARY_40,
    color: DGII_TEXT,
  },
  changeCardBillet: {
    backgroundColor: BILLET_BACKGROUND,
    borderColor: BILLET_BORDER,
    color: BILLET_TEXT,
  },
  changeCardCardnet: {
    backgroundColor: CARDNET_BACKGROUND,
    borderColor: CARDNET_BORDER,
    color: CARDNET_TEXT,
  },
  changeCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  changeCardTitle: {
    fontWeight: 600,
    color: 'inherit',
  },
  changeRow: {
    display: 'flex',
    gap: theme.spacing(1),
    fontSize: '0.8125rem',
    lineHeight: 1.5,
  },
  changeRowLabel: {
    color: BLACK_40,
    whiteSpace: 'nowrap',
  },
  changeRowValue: {
    fontWeight: 700,
    color: BLACK,
  },
  changeRowValueAdverse: {
    fontWeight: 700,
    color: DANGER_TEXT,
  },
  changeRowValueFavorable: {
    fontWeight: 700,
    color: DGII_TEXT,
  },
  modalActions: {
    padding: theme.spacing(2, 4, 3),
    gap: theme.spacing(2),
  },
  // Los dos botones reparten el ancho a partes iguales, como en el diseño. `minWidth: 0` apaga
  // el tamaño mínimo automático del flex item: sin él ninguno baja de su ancho de contenido y
  // "Revisar solicitud", al tener más texto, sale más ancho que "Cancelar". Se ancla a
  // `&.MuiButton-root` para ganarle al `min-width: 64px` que trae el botón por defecto.
  modalCancelButton: {
    '&.MuiButton-root': {
      flex: '1 1 0',
      minWidth: 0,
      textTransform: 'initial',
      color: BLACK_80,
      borderColor: BLACK_10,
    },
  },
  modalConfirmButton: {
    '&.MuiButton-root': {
      flex: '1 1 0',
      minWidth: 0,
      textTransform: 'initial',
      backgroundColor: PRIMARY,
      color: WHITE,
      '&:hover': {
        backgroundColor: PRIMARY_80,
      },
    },
  },
}));

export default useStyle;
